import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { LocationTermsService } from '../api/location-terms.service';

/**
 * De onde o modal foi aberto. Define qual texto o componente mostra:
 *  - 'geral': privacidade do app como um todo (inclui o segundo plano).
 *  - 'ronda': focado na localização durante a ronda.
 */
export type ContextoTermo = 'geral' | 'ronda';

/**
 * Controla a exibição do termo de uso de localização.
 *
 * O aceite é gravado no banco, por usuário. Fluxo:
 *  - No primeiro acesso (nenhum aceite registrado), o modal aparece sobre
 *    qualquer tela em que o usuário esteja, com o texto geral.
 *  - Se recusar, o modal volta a aparecer ao entrar na tela de ronda, com o
 *    texto focado em localização.
 *  - Quem já aceitou não vê mais o modal.
 */
@Injectable({ providedIn: 'root' })
export class LocationConsentService {
    private readonly termsApi = inject(LocationTermsService);

    private readonly aceito = signal<boolean>(false);
    private readonly modalAberto = signal<boolean>(false);
    private readonly contextoAtual = signal<ContextoTermo>('geral');

    readonly aberto = this.modalAberto.asReadonly();
    readonly contexto = this.contextoAtual.asReadonly();

    /** Abre o modal se o usuário ainda não aceitou. Usado no primeiro acesso. */
    async verificar(): Promise<void> {
        try {
            const estado = await firstValueFrom(this.termsApi.buscarEstado());
            this.aceito.set(estado.accepted);
        } catch (error) {
            // Falha de rede não pode esconder o termo: mantém `aceito` false e
            // abre, igual a ronda faz. Pior caso, quem já aceitou vê de novo.
            console.error('❌ Falha ao buscar aceite do termo de localização:', error);
        }

        if (!this.aceito()) {
            this.contextoAtual.set('geral');
            this.modalAberto.set(true);
        }
    }

    /**
     * Reabre o termo ao entrar na ronda, para quem recusou antes.
     *
     * Usa o estado já conhecido em memória — se o usuário aceitou nesta sessão,
     * não há motivo para consultar o servidor de novo.
     */
    abrirSeNaoAceito(): void {
        if (!this.aceito()) {
            this.contextoAtual.set('ronda');
            this.modalAberto.set(true);
        }
    }

    async aceitar(): Promise<void> {
        try {
            const estado = await firstValueFrom(this.termsApi.aceitar());
            this.aceito.set(estado.accepted);
        } catch (error) {
            console.error('❌ Falha ao registrar aceite do termo de localização:', error);
            return;
        }

        this.modalAberto.set(false);
    }

    async recusar(): Promise<void> {
        try {
            await firstValueFrom(this.termsApi.recusar());
        } catch (error) {
            // Fechar de qualquer jeito: prender o usuário numa tela que ele
            // acabou de dispensar seria pior que perder o registro da recusa.
            console.error('❌ Falha ao registrar recusa do termo de localização:', error);
        }

        this.aceito.set(false);
        this.modalAberto.set(false);
    }

    /** Limpa o estado no logout — o aceite é por usuário. */
    limparEstado(): void {
        this.aceito.set(false);
        this.modalAberto.set(false);
    }
}
